// "My private documents" in Doc Control (owner 2026-11-01): documents only
// the signed-in person can see - a pay slip, a personal certificate, a
// contract with personal terms - kept apart from the project register
// (back/routes/privateDocuments.js). Shown only when the person has any, or
// when they add one.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Popconfirm, Tag, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { get, url, tokenRef } from "api/index";

const kb = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

export default function PrivateDocuments({ projectId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    get(`/doc-control/private${projectId ? `?projectId=${projectId}` : ""}`).then((r) => setRows((r && r.documents) || [])).catch(() => setRows([]));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
  const openFile = async (row, download) => {
    const r = await fetch(`${url}/doc-control/private/${row._id}/file${download ? "?download=1" : ""}`, { headers: auth() });
    if (!r.ok) { message.error("Could not open the document"); return; }
    const href = URL.createObjectURL(await r.blob());
    if (download) { const a = document.createElement("a"); a.href = href; a.download = row.name; a.click(); setTimeout(() => URL.revokeObjectURL(href), 5000); }
    else window.open(href, "_blank", "noopener");
  };
  const remove = async (row) => {
    const r = await fetch(`${url}/doc-control/private/${row._id}`, { method: "DELETE", headers: auth() });
    if (r.ok) { message.success("Deleted"); load(); } else message.error("Could not delete");
  };
  const add = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    if (projectId) body.append("projectId", projectId);
    const r = await fetch(`${url}/doc-control/private`, { method: "POST", headers: auth(), body });
    const j = await r.json().catch(() => ({}));
    if (r.ok) { message.success("Saved privately - only you can see it"); load(); } else message.error(j.err || "Could not save");
  };

  return (
    <div style={{ marginTop: 10, border: "1px solid #e6e9ee", borderRadius: 8, padding: "8px 12px", background: "#fafbfc" }}>
      <div className="d-flex align-items-center justify-content-between" style={{ gap: 8 }}>
        <a onClick={() => setOpen((v) => !v)} style={{ fontWeight: 600, color: "#3d5a72" }}>
          <LockOutlined /> My private documents {rows.length ? `(${rows.length})` : ""} <span style={{ fontWeight: 400, color: "#8a9aa8", fontSize: 12 }}>- only you can see these</span>
        </a>
        <span>
          <input ref={fileRef} type="file" style={{ display: "none" }} onChange={(e) => { add(e.target.files[0]); e.target.value = ""; }} />
          <Button size="small" icon={<LockOutlined />} onClick={() => fileRef.current && fileRef.current.click()}>Add private document</Button>
        </span>
      </div>
      {open && rows.length ? (
        <table className="bid-table bid-table-small" style={{ marginTop: 8 }}>
          <thead><tr><th>Name</th><th>What it is</th><th>Size</th><th>Added</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id}>
                <td><a onClick={() => openFile(r, false)}>{r.name}</a>{r.source === "gpt" ? <Tag style={{ marginLeft: 6 }}>from BidLight GPT</Tag> : null}</td>
                <td style={{ maxWidth: 360 }}>{r.title || r.description || ""}</td>
                <td>{kb(r.bytes)}</td>
                <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <a onClick={() => openFile(r, true)}>Download</a>{" "}
                  <Popconfirm title="Delete this private document?" onConfirm={() => remove(r)}><a style={{ color: "#a33", marginLeft: 8 }}>Delete</a></Popconfirm>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
